import React, {useContext, useRef} from "react";
import {Controller, useForm} from "react-hook-form";
import {EMAIL_REGEX} from "../../../../constants";
import {Button, Checkbox, Container, FormControl, FormControlLabel} from "../../../../ui";
import {FormHelperText, TextField} from "../../../bounce-ui";
import {PaymentsImages} from "../../../components";
import {PaymentDetails, PaymentType} from "../../../declarations";
import {TermsType} from "../terms-and-conditions/components";
import {TermsAndConditionsApproval} from "../terms-and-conditions/TermsAndConditionsApproval";
import {PaymentContext} from "../utils";
import {ApplePayButton} from "./ApplePayButton";
import {LargePaymentSplitLabel} from "./LargePaymentsSplitLabel";
import {PaymentFormCommonProps} from "./types";
import {buildCardDefaults} from "./utils";

interface ApplePayPaymentFormProps extends PaymentFormCommonProps {
  onCardSubmit: (paymentType: PaymentType, paymentDetails: PaymentDetails, isChangePaymentMethod: boolean) => void;
  termsAndConditionsId?: string;
  isChangePaymentMethod: boolean;
  merchantSiteId: string;
  primaryEmail?: string;
  userName?: string;
  showEmailField?: boolean;
  backToDashboardButton?: boolean;
  handleBackToDashboard?: () => void;
  paymentDetails?: PaymentDetails;
  totalAmount: string;
  buttonRadius?: string;
}

export type FormValues = {
  email?: string;
  confirmTerms: boolean;
};

export const ApplePayPaymentForm = ({
  onCardSubmit,
  termsAndConditionsId,
  isChangePaymentMethod,
  merchantSiteId,
  primaryEmail,
  userName,
  showEmailField = true,
  backToDashboardButton = false,
  handleBackToDashboard,
  paymentDetails,
  totalAmount,
  pageClickEvent,
  pageViewEvent,
  Pages,
  PaymentTermsAndConditionsV2,
  TermsAndConditionsDialog,
  partnerDetails,
  fpPartnerName,
  buttonRadius = "999px",
}: ApplePayPaymentFormProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const defaultValues = buildCardDefaults(paymentDetails, userName, primaryEmail);

  const {
    control,
    getValues,
    formState: {isValid},
  } = useForm<FormValues>({
    defaultValues,
  });

  const {buttonTouched, showLargeSplitLabel, disableButton} = useContext(PaymentContext);

  return (
    <div ref={ref} style={{width: "100%", height: "100%"}}>
      <form autoComplete="off" noValidate>
        <Container sx={{gap: "20px"}}>
          {showLargeSplitLabel && <LargePaymentSplitLabel />}
          {showEmailField && (
            <Controller
              name="email"
              rules={{
                pattern: {value: EMAIL_REGEX, message: "Email invalid"},
              }}
              control={control}
              render={({field: {ref, ...field}, fieldState: {invalid, error}}) => {
                return (
                  <TextField
                    {...field}
                    inputRef={ref}
                    fullWidth
                    label={"Email address for receipts"}
                    type="email"
                    error={invalid}
                    helperText={error?.message}
                    inputProps={{
                      maxLength: 50,
                    }}
                  />
                );
              }}
            />
          )}
          <PaymentsImages />
          <Controller
            name="confirmTerms"
            rules={{
              required: "Required field",
            }}
            control={control}
            render={({field: {ref, value, ...field}, fieldState: {invalid, error}}) => {
              return (
                <FormControl required error={invalid}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...field}
                        inputRef={ref}
                        size={"small"}
                        checked={value}
                        sx={{alignSelf: "flex-start", marginTop: "0px"}}
                      />
                    }
                    label={
                      <TermsAndConditionsApproval
                        termsAndConditionsId={termsAndConditionsId || TermsType.PAYMENT}
                        page={Pages.PAYMENT_SUMMARY}
                        pageClickEvent={pageClickEvent}
                        pageViewEvent={pageViewEvent}
                        page_T_C={Pages.T_C}
                        TermsAndConditionsDialog={TermsAndConditionsDialog}
                        PaymentTermsAndConditionsV2={PaymentTermsAndConditionsV2}
                        partnerDetails={partnerDetails}
                        fpPartnerName={fpPartnerName}
                        sx={{textAlign: "start"}}
                      />
                    }
                    sx={{alignItems: "flex-start"}}
                  />
                  <FormHelperText>{error?.message}</FormHelperText>
                </FormControl>
              );
            }}
          />
          <Container fullWidth sx={{gap: "8px"}}>
            <ApplePayButton
              buttonStyle="black"
              disabled={buttonTouched || disableButton || !isValid}
              onCardSubmit={onCardSubmit}
              totalAmount={totalAmount}
              merchantSiteId={merchantSiteId}
              isChangePaymentMethod={isChangePaymentMethod}
              formValues={getValues()}
              label={!!partnerDetails && !!partnerDetails.partnerName ? partnerDetails.partnerName : "Bounce AI, Inc."}
              buttonRadius={buttonRadius}
            />
            {backToDashboardButton && (
              <Button buttonType={"contained-secondary"} onClick={handleBackToDashboard} fullWidth>
                Back to dashboard
              </Button>
            )}
          </Container>
        </Container>
      </form>
    </div>
  );
};
